import { type HeroTypeFront } from '@Types/Blocks/Hero';

import Decorations from '../../_components/Decorations';
import HeroClassic from './variants/Classic';
import HeroRond from './variants/Rond';
import HeroSuperPosition from './variants/Superposition';
import HeroText from './variants/Text';

/**
 * The Hero component is a customizable and impactful element for showcasing key content at the top of your website.
 */
export const Hero = (props: HeroTypeFront) => {
	const {
		block_id,
		title,
		description,
		image,
		img_position,
		ctas,
		ctasStyle,
		titleFormat,
		variant,
		containerType,
		context = 'hero',
		style,
		placement,
		sectionDecoration,
	} = props;

	const HeroProps: HeroTypeFront = {
		id: block_id,
		title: title,
		description: description,
		image: image,
		img_position: img_position,
		ctas: ctas,
		ctasStyle: ctasStyle,
		titleFormat: titleFormat,
		variant: variant,
		containerType: containerType,
		context: context,
		sectionDecoration: sectionDecoration,
		contentFontClass: {
			title: 'minos-hero-title-font-style tablet:minos-hero-title-font-style ',
			text: {
				p: 'minos-hero-text-font-style text-base tablet:minos-hero-text-font-style my-4',
				a: {
					style: 'a',
					btnHasIcon: ctasStyle?.a?.btnHasIcon ? ctasStyle?.a?.btnHasIcon : false,
					btnDecorationElement: 'underline',
					aClassname: 'minos-hero-btn-a text-base tablet:minos-hero-btn-a',
				},
				btnPrimary: {
					style: 'primary',
					btnDecorationElement: ctasStyle?.primary?.btnDecorationElement
						? ctasStyle?.primary?.btnDecorationElement
						: 'long',
					btnHasIcon: ctasStyle?.primary?.btnHasIcon ? ctasStyle?.primary?.btnHasIcon : false,
					primaryClassname: 'minos-hero-btn-primary text-base tablet:minos-hero-btn-primary',
					animation: ctasStyle?.primary?.animation ? ctasStyle?.primary?.animation : 'none',
				},
				btnSecondary: {
					style: 'secondary',
					btnDecorationElement: ctasStyle?.secondary?.btnDecorationElement
						? ctasStyle?.secondary?.btnDecorationElement
						: 'long',
					btnHasIcon: ctasStyle?.secondary?.btnHasIcon ? ctasStyle?.secondary?.btnHasIcon : false,
					secondaryClassname: 'minos-hero-btn-secondary text-base tablet:minos-hero-btn-secondary',
					animation: ctasStyle?.secondary?.animation ? ctasStyle?.secondary?.animation : 'none',
				},
				btnTertiary: {
					style: 'tertiary',
					btnDecorationElement: ctasStyle?.tertiary?.btnDecorationElement
						? ctasStyle?.tertiary?.btnDecorationElement
						: 'long',
					btnHasIcon: ctasStyle?.tertiary?.btnHasIcon ? ctasStyle?.tertiary?.btnHasIcon : false,
					tertiaryClassname: 'minos-hero-btn-tertiary text-base tablet:minos-hero-btn-tertiary',
					animation: ctasStyle?.tertiary?.animation ? ctasStyle?.tertiary?.animation : 'none',
				},
			},
		},
	};

	const variations = {
		classic: <HeroClassic {...HeroProps} />,
		rond: <HeroRond {...HeroProps} />,
		text: <HeroText {...HeroProps} />,
		superposition: <HeroSuperPosition {...HeroProps} />,
	};

	const variationRondImgBg = img_position === 'bg' && variant === 'rond';
	const variationTxt = variant === 'text' && containerType === 'block' && img_position === 'center';
	const variationSuperposition = variant === 'superposition' && img_position === 'bg';

	return (
		<section style={style} data-variant={`hero-${variant}`} data-position={`img-${img_position}`}>
			<div className='relative bg-minos-hero-bg-color'>
				{/* No decoration wave if variant === text */}
				{variant != 'text' ? (
					<>
						{/*  Decoration wave has an impact on the img_position and the container type block */}
						{img_position != 'bg' && containerType === 'wide' ? (
							<Decorations
								isSectionDecorated={sectionDecoration?.isSectionDecorated}
								type={sectionDecoration?.type}
								pathClassName={`${sectionDecoration?.type === 'wave' ? 'fill-minos-hero-bg-color' : 'fill-minos-decoration-bubble-left'}`}
								pathClassNameRightCircle={`${sectionDecoration?.type === 'bubble' && 'fill-minos-decoration-bubble-right'}`}
								placement={placement}
							>
								{variations[variant]}
							</Decorations>
						) : (
							<>
								{containerType === 'block' && !variationRondImgBg && !variationSuperposition ? (
									<Decorations
										isSectionDecorated={sectionDecoration?.isSectionDecorated}
										type={sectionDecoration?.type}
										pathClassName={`${sectionDecoration?.type === 'wave' ? 'fill-minos-hero-bg-color' : 'fill-minos-decoration-bubble-left'}`}
										pathClassNameRightCircle={`${sectionDecoration?.type === 'bubble' && 'fill-minos-decoration-bubble-right'}`}
										placement={placement}
									>
										{variations[variant]}
									</Decorations>
								) : (
									<div className='bg-minos-hero-bg-color'>{variations[variant]}</div>
								)}
							</>
						)}
					</>
				) : (
					<>
						{/* Exception on variant === text if containerType === 'block' && img_position === 'center' */}
						{variationTxt ? (
							<Decorations
								isSectionDecorated={sectionDecoration?.isSectionDecorated}
								type={sectionDecoration?.type}
								pathClassName={`${sectionDecoration?.type === 'wave' ? 'fill-minos-hero-bg-color' : 'fill-minos-decoration-bubble-left'}`}
								pathClassNameRightCircle={`${sectionDecoration?.type === 'bubble' && 'fill-minos-decoration-bubble-right'}`}
								placement={placement}
							>
								{variations[variant]}
							</Decorations>
						) : (
							<div className='bg-minos-hero-bg-color'>{variations[variant]}</div>
						)}
					</>
				)}
			</div>
		</section>
	);
};

export default Hero;
